import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, PieChart, Pie, Legend,
} from "recharts";
import { TrendingUp, TrendingDown, DollarSign, Store, Target as TargetIcon, AlertCircle, Package, Download } from "lucide-react";
import { toast } from "sonner";

const BRAND = ["#003DA5", "#4070BC", "#001F54", "#FFB547", "#809ED2", "#BFCFE8", "#2E8B57", "#D9534F"];

export default function SalesIntelligenceDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/sales-dashboard").then(r => setD(r.data)); }, []);
  if (!d) return <div className="text-slate-500">Loading sales dashboard…</div>;
  const k = d.kpis;

  const doExport = async (fmt) => {
    try {
      const res = await api.get(`/reports/sales_dashboard`, { params: { fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `MLILY-Sales-Dashboard.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-6" data-testid="sales-dashboard-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">Sales Dashboard</h1>
          <p className="text-slate-600 mt-1">Company-wide performance, coverage & growth signals</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => doExport("xlsx")} className="h-11 px-4 border border-slate-300 rounded-lg font-semibold text-sm flex items-center gap-2 hover:border-[#003DA5]" data-testid="export-dashboard-xlsx"><Download className="w-4 h-4" /> Excel</button>
          <button onClick={() => doExport("pdf")} className="h-11 px-4 border border-slate-300 rounded-lg font-semibold text-sm flex items-center gap-2 hover:border-[#003DA5]" data-testid="export-dashboard-pdf"><Download className="w-4 h-4" /> PDF</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <Kpi label="Gross Sales (Mo)" value={money(k.gross_sales)} icon={DollarSign} testid="kpi-gross" />
        <Kpi label="Credit Notes (Mo)" value={money(k.credit_notes)} tone="red" testid="kpi-credits" />
        <Kpi label="Net Sales (Mo)" value={money(k.net_sales)} tone="blue" testid="kpi-net" />
        <Kpi label="Current Target" value={money(k.target)} icon={TargetIcon} testid="kpi-target" />
        <Kpi label="Achieved" value={money(k.achieved)} tone="green" testid="kpi-achieved" />
        <Kpi label="Remaining to Target" value={money(k.target_remaining)} tone="red" testid="kpi-remaining" />
        <Kpi label="% of Target" value={`${k.target_pct}%`} tone={k.target_pct >= 100 ? "green" : k.target_pct >= 60 ? "blue" : "red"} testid="kpi-target-pct" />
        <Kpi label="YoY YTD Growth" value={k.yoy_growth_pct == null ? "—" : `${k.yoy_growth_pct >= 0 ? "+" : ""}${k.yoy_growth_pct}%`} tone={k.yoy_growth_pct == null ? "neutral" : k.yoy_growth_pct >= 0 ? "green" : "red"} icon={k.yoy_growth_pct >= 0 ? TrendingUp : TrendingDown} testid="kpi-yoy" />
        <Kpi label="Active Customers" value={k.active_customers} icon={Store} testid="kpi-active-cust" />
        <Kpi label="Inactive Customers" value={k.inactive_customers} tone="neutral" testid="kpi-inactive-cust" />
        <Kpi label="Declining Stores" value={k.declining_stores} tone="red" icon={TrendingDown} testid="kpi-declining" />
        <Kpi label="No Recent Orders" value={k.no_recent_orders} tone="amber" icon={AlertCircle} testid="kpi-no-recent" />
        <Kpi label="Low Coverage Products" value={k.low_coverage_products} icon={Package} tone="amber" testid="kpi-low-cov" />
        <Kpi label="Strong-Growth Products" value={k.strong_growth_products} icon={TrendingUp} tone="green" testid="kpi-strong" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Monthly Sales Trend">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={d.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={v => money(v)} tick={{ fontSize: 11 }} />
              <Tooltip formatter={v => money(v)} />
              <Line type="monotone" dataKey="value" stroke="#003DA5" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Sales by State">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={d.by_state} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => money(v)} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={60} />
              <Tooltip formatter={v => money(v)} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>{d.by_state.map((_, i) => <Cell key={i} fill={BRAND[i % BRAND.length]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Sales by Sales Agent">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={d.by_agent} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => money(v)} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={110} />
              <Tooltip formatter={v => money(v)} />
              <Bar dataKey="value" fill="#001F54" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Sales by Product">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={d.by_product} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => money(v)} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={140} />
              <Tooltip formatter={v => money(v)} />
              <Bar dataKey="value" fill="#4070BC" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Sales by Customer Group">
          {d.by_group.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={d.by_group} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.name}: ${money(e.value)}`}>
                  {d.by_group.map((_, i) => <Cell key={i} fill={BRAND[i % BRAND.length]} />)}
                </Pie>
                <Tooltip formatter={v => money(v)} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Floor-Display Coverage by Product">
          <ResponsiveContainer width="100%" height={Math.max(220, d.coverage_by_product.length * 26)}>
            <BarChart data={d.coverage_by_product} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
              <YAxis dataKey="product_name" type="category" tick={{ fontSize: 11 }} width={140} />
              <Tooltip formatter={v => `${v}%`} />
              <Bar dataKey="coverage_pct" radius={[0, 6, 6, 0]}>{d.coverage_by_product.map((r, i) => <Cell key={i} fill={r.coverage_pct >= 70 ? "#2E8B57" : r.coverage_pct >= 40 ? "#003DA5" : "#D9534F"} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Current Year vs Previous Year">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={[{ name: "Last Year", value: k.ly_ytd }, { name: "This Year", value: k.ytd }]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={v => money(v)} tick={{ fontSize: 11 }} />
              <Tooltip formatter={v => money(v)} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                <Cell fill="#BFCFE8" />
                <Cell fill="#003DA5" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Sales vs Target">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={[{ name: "Achieved", value: k.achieved }, { name: "Target", value: k.target }]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={v => money(v)} tick={{ fontSize: 11 }} />
              <Tooltip formatter={v => money(v)} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                <Cell fill={k.achieved >= k.target ? "#2E8B57" : "#003DA5"} />
                <Cell fill="#001F54" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {d.strong_products.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="strong-growth-block">
          <h3 className="text-lg font-heading font-semibold mb-3 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-600" /> Strong Growth Products</h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {d.strong_products.map(p => (
              <li key={p.name} className="flex justify-between p-2 bg-emerald-50 border border-emerald-200 rounded">
                <span className="font-medium">{p.name}</span>
                <span className="text-emerald-700 font-bold">+{p.growth_pct}% • {money(p.ytd)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const Kpi = ({ label, value, tone = "default", icon: Icon, testid }) => {
  const c = tone === "green" ? "text-emerald-600" : tone === "red" ? "text-red-600" : tone === "blue" ? "text-[#003DA5]" : tone === "amber" ? "text-amber-600" : tone === "neutral" ? "text-slate-500" : "text-[#001F54]";
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-semibold text-slate-500">
        <span>{label}</span>
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
      </div>
      <div className={`text-2xl font-heading font-bold mt-1.5 ${c}`}>{value}</div>
    </div>
  );
};
const ChartCard = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <h3 className="text-lg font-heading font-semibold mb-3">{title}</h3>
    {children}
  </div>
);
const Empty = () => <div className="text-sm text-slate-500 text-center py-8">No data.</div>;
